import { Experiment } from '@amplitude/experiment-js-client';
import type { ExperimentClient } from '@amplitude/experiment-js-client';
import { getConfig } from '@config';
import { readAnonymousIdFromCookie } from '@shared/core/featureFlags/remoteFeatureFlags/amplitude.utils';
import { addAction } from '@shared/utils/logger';

/**
 * Resolves the auth host for `authorize`, `changePassword` and logout from the
 * `auth-domain-joy-web-social-logout` Amplitude flag. The key still says "social" for historical
 * reasons; social login was moved out from under it and pinned to `withjoy.auth0.com`.
 *
 * This is a DEDICATED flag for joy-web's legacy `auth0.ts` flows — deliberately separate from the
 * `auth-domain-clients` flag that `/account-login` (`useAuthClient`) and carbon read, so ramping it
 * cannot affect those existing flows. Same `{ domain }` payload shape.
 *
 * Reads the flag via the Amplitude **Experiment client SDK** (the same mechanism `useAuthClient` and
 * carbon use), NOT the SSR-embedded `window.__INITIAL_AMPLITUDE_FLAGS__`. The SSR snapshot is only
 * written on guest-site routes, whereas these flows fire on `/account`, card, createWedding and
 * `/logout`; the SDK fetches client-side on every route, so it actually takes effect.
 *
 * Returns the flag's `payload.domain` verbatim, or `null` when unavailable — flag unset/malformed,
 * fetch error or timeout, or running on the server. Callers fall back to today's Auth0 host, so a
 * flag-SDK gap silently preserves current behavior.
 *
 * NOTE: login and signup are intentionally NOT covered. They use auth0-js embedded auth
 * (`/co/authenticate`, `/dbconnections/signup`), which the auth proxy does not implement, so they
 * stay on the Auth0 custom domain. Social login is not covered either: it is pinned to
 * `withjoy.auth0.com` because the legacy app reads its session there, and because a proxied call
 * reaches Auth0 from our egress IP, which draws an MFA challenge a social user cannot answer.
 * See `auth0.ts`.
 */
const FLAG_KEY = 'auth-domain-joy-web-social-logout';
const FETCH_TIMEOUT_MS = 2000;

// Distinct sentinel so the caller can tell "flag server timed out" apart from "fetch resolved but
// the flag isn't set" — both fall back to the Auth0 host, but they're different diagnostics.
const TIMEOUT = Symbol('auth-domain-fetch-timeout');

const raceWithTimeout = async <T>(promise: Promise<T>, ms: number): Promise<T | typeof TIMEOUT> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<typeof TIMEOUT>(resolve => {
    timer = setTimeout(() => resolve(TIMEOUT), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  }
};

const fetchAuthDomain = async (): Promise<string | null> => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const config = getConfig();
    // `Experiment.initialize` caches ONE client per `instanceName + "." + apiKey` and ignores the
    // config of any later caller for that key. A dedicated `instanceName` keeps this a genuinely
    // separate client from the app's default-instance flag SDK (`useAmplitudeExperiment`, which uses
    // the same apiKey + default instance): without it, whichever initializes first wins and clobbers
    // the other's config (SSR initialVariants, exposure provider, automaticExposureTracking…) — an
    // app-wide flag-SDK regression — and the two share one fetch/user.
    const client: ExperimentClient = Experiment.initialize(config.amplitudeExperimentApiKey, {
      instanceName: 'auth-domain',
      serverUrl: config.amplitudeExperimentServer,
      flagsServerUrl: config.amplitudeExperimentFlagsServer,
      automaticExposureTracking: false,
    });
    // Bucket on the shared anonymous id (raw cookie, same reader SSR + the exposure tracker use).
    // This gives device-scoped consistency — it matches logged-out social login (the app also buckets
    // on device id there); changePassword/logout fire while logged in, where the app additionally
    // buckets on user id, so target this flag on device id (or accept device-scoped assignment).
    // NB: NOT the prefixing CookieStorage — that would look up `joy<name>` and never match.
    const deviceId = readAnonymousIdFromCookie();
    // Bounded so a slow/unreachable flag server can never block auth construction (`initAuth0`
    // queues every webAuth action until this settles). On timeout we read whatever the client has,
    // which yields `null` and falls back.
    const raced = await raceWithTimeout(client.fetch(deviceId ? { device_id: deviceId } : {}), FETCH_TIMEOUT_MS);
    const variant = client.variant(FLAG_KEY);
    const domain = (variant?.payload as { domain?: unknown } | undefined)?.domain;
    if (typeof domain === 'string' && domain.length > 0) {
      // Fire-and-forget New Relic PageAction (no await, no PII — hostname only). Lets us confirm on
      // dev which host each load resolved to for the four proxied flows, incl. the redirect ones
      // (social login, logout) that never surface as an AjaxRequest.
      addAction('authDomainResolved', { outcome: 'proxy', domain });
      return domain;
    }
    addAction('authDomainResolved', { outcome: raced === TIMEOUT ? 'timeout' : 'fallback' });
    return null;
  } catch {
    addAction('authDomainResolved', { outcome: 'error' });
    return null;
  }
};

let resolvePromise: Promise<string | null> | null = null;

/**
 * The flag-resolved auth host (memoized — evaluated at most once per page load), or `null` when the
 * flag is unavailable (caller falls back to today's Auth0 host).
 */
export const resolveAuthDomainFromFlag = (): Promise<string | null> => {
  if (!resolvePromise) {
    resolvePromise = fetchAuthDomain();
  }
  return resolvePromise;
};
